import React, { useEffect, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  changeBatchBulk,
  changeBatchForInduvidualStudent,
  changeBatchStatus,
  getAllBatchNameNoPagination,
  getAllBatchNameNoPaginationForBulkChange,
  getBatchesList,
  getCardsData,
  getStudentsUnderBatch,
  getTutorsList,
} from "../../redux/features/batchSlice";
import * as Yup from "yup";
import GlobalDialog from "../../components/mui/Dialogue/GlobalDialog";

const ChangeBatch = ({ rowData, dispatchData, open, setOpen }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { loading, courseBatches, allcourses } = useSelector(
    (state) => state.batch
  );
  const [students, setStudents] = useState([]);
  const [courseID, setCourseID] = useState(null);

  const validationSchema = Yup.object().shape({
    batch_ids: Yup.array().min(1, "Select at least one batch"),
    newBatchId: Yup.mixed().nullable().required("Select New Batch"),
    students: Yup.array().min(1, "Choose at least one student"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      batch_ids: [],
      newBatchId: null,
      students: [],
    },
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        batch_assignment_id: values.students.map((b) => b.batch_assignment_id),
        batchId: values.newBatchId.course_batch_id,
      };

      dispatch(changeBatchForInduvidualStudent(payload)).then((res) => {
        if (res.payload.status === "success") {
          enqueueSnackbar(res.payload.message, { variant: "success" });
          dispatch(getCardsData());
          dispatch(getBatchesList(dispatchData));
          setOpen(false);
        } else {
          enqueueSnackbar(res.payload.message, { variant: "error" });
        }
      });
    },
  });

  useEffect(() => {
    if (formik.values.batch_ids.length > 0) {
      const batch_ids = formik.values.batch_ids.map((b) => b.course_batch_id);
      dispatch(getStudentsUnderBatch({ batch_ids })).then((res) => {
        if (res.payload?.status === "success") {
          setStudents(res.payload.data || []);
        } else {
          setStudents([]);
        }
      });
    } else {
      setStudents([]);
    }
  }, [formik.values.batch_ids]);

  const data = {
    status: false,
    courseId: courseID?.course_available_id || null,
  };

  useEffect(() => {
    if (open && courseID?.course_available_id) {
      dispatch(getAllBatchNameNoPaginationForBulkChange(data));
    }
  }, [open, courseID?.course_available_id]);

  useEffect(() => {
    if (rowData?.course_available_id && allcourses?.length > 0) {
      const selectedCourse = allcourses.find(
        (course) => course.course_available_id === rowData.course_available_id
      );
      setCourseID(selectedCourse || null);
    }
  }, [rowData, allcourses]);

  return (
    <GlobalDialog
      title={"Change Batch"}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={"sm"}
      closeButton={true}
      content={
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Box>
              <Stack direction={"column"} spacing={2}>
                <Autocomplete
                  options={allcourses}
                  getOptionLabel={(option) => option.course_name || ""}
                  value={courseID}
                  onChange={(e, value) => setCourseID(value)}
                  isOptionEqualToValue={(option, value) =>
                    option.course_available_id === value?.course_available_id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Current Course"
                      placeholder="Choose course"
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={courseBatches}
                  getOptionLabel={(option) => option.course_batch_name || ""}
                  value={formik.values.batch_ids}
                  onChange={(e, value) => {
                    formik.setFieldValue("batch_ids", value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Choose Batch"
                      placeholder={`Choose batch under ${
                        courseID?.course_name || ""
                      }`}
                      error={
                        formik.touched.batch_ids &&
                        Boolean(formik.errors.batch_ids)
                      }
                      helperText={
                        formik.touched.batch_ids && formik.errors.batch_ids
                      }
                    />
                  )}
                />
                {formik.values.batch_ids.length > 0 &&
                  students.length === 0 && (
                    <Alert severity="warning" variant="outlined">
                      No students in this batch.
                    </Alert>
                  )}
                <Autocomplete
                  multiple
                  disabled={students.length === 0}
                  options={students}
                  getOptionLabel={(option) =>
                    option.studentName && option.phoneNumber
                      ? `${option.studentName} - ${option.phoneNumber}`
                      : option.studentName || ""
                  }
                  value={formik.values.students}
                  onChange={(e, value) =>
                    formik.setFieldValue("students", value)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Students in Batch"
                      placeholder="Select students"
                      error={
                        formik.touched.students &&
                        Boolean(formik.errors.students)
                      }
                      helperText={
                        formik.touched.students && formik.errors.students
                      }
                    />
                  )}
                />
                {students?.length > 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          formik.values.students.length === students.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            formik.setFieldValue("students", students);
                          } else {
                            formik.setFieldValue("students", []);
                          }
                        }}
                      />
                    }
                    label="Select All Students"
                  />
                )}
                <Autocomplete
                  options={courseBatches}
                  getOptionLabel={(option) => option.course_batch_name || ""}
                  value={formik.values.newBatchId}
                  onChange={(e, value) =>
                    formik.setFieldValue("newBatchId", value)
                  }
                  getOptionDisabled={(option) =>
                    formik.values.batch_ids.some(
                      (b) => b.course_batch_id === option.course_batch_id
                    )
                  }
                  onBlur={() => formik.setFieldTouched("newBatchId", true)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select New Batch"
                      placeholder="Choose new batch to assign"
                      error={
                        formik.touched.newBatchId &&
                        Boolean(formik.errors.newBatchId)
                      }
                      helperText={
                        formik.touched.newBatchId && formik.errors.newBatchId
                      }
                    />
                  )}
                />
                <Stack direction={"row"} spacing={1}>
                  {formik.values.newBatchId?.subjects?.map((sub) => (
                    <Chip
                      label={sub.subject_name}
                      variant="filled"
                      color="primary"
                      key={sub.subject_id}
                    />
                  ))}
                </Stack>
              </Stack>

              <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                <Stack direction={"row"} spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => setOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={loading}
                    variant="contained"
                    color="secondary"
                    size="small"
                    type="submit"
                  >
                    {loading ? "Please wait..." : "Save"}
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      }
    />
  );
};

export default ChangeBatch;
